import {
  Badge,
  Box,
  ButtonBase,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  Divider,
} from '@mui/material';

import { useUser } from '../../hooks/useUser';
import { useState } from 'react';
import { parseChangelog } from './parseChangelog';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useLingui } from '@lingui/react/macro';

type ChangeLogButtonProps = {
  size?: 'small' | 'large';
};

import changeLog from '../../../changeLog.md?raw';
import { format } from 'date-fns';
import Markdown from 'react-markdown';

const getVersionFromChangeLog = (log: string): string => {
  const match = log.match(/\[(.*?)\]/);
  return match ? match[1] : '0.0.0';
};

const latestVersion = getVersionFromChangeLog(changeLog);

const ChangeLogButton = ({ size }: ChangeLogButtonProps) => {
  const { t: trans } = useLingui();
  const { details, saveUserUIDetails } = useUser();
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const lastSeenVersion = details?.lastSeenVersion || '0.0.0';

  const handleOpen = () => {
    saveUserUIDetails({
      lastSeenVersion: latestVersion,
    });
    setChangeLogOpen(true);
  };

  const handleClose = () => {
    setChangeLogOpen(false);
  };

  const [lastMajor, lastMinor] = lastSeenVersion.split('.').map(Number);
  const [latestMajor, latestMinor] = latestVersion.split('.').map(Number);
  const showBadge =
    lastMajor < latestMajor || (lastMajor === latestMajor && lastMinor < latestMinor);

  // Parse changelog
  const entries = parseChangelog(changeLog);
  const [firstEntry] = entries;
  const { version, date, content } = firstEntry;
  return (
    <>
      <ButtonBase onClick={handleOpen} sx={{ justifyContent: 'flex-start', height: '22px' }}>
        <Badge
          color="primary"
          variant="dot"
          invisible={!showBadge}
          sx={{
            marginRight: size === 'large' ? '8px' : 0,
            transition: 'margin-right 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              paddingRight: '8px',
            }}
          >
            <Typography
              variant="caption1"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                position: 'absolute',
                clipPath: size === 'large' ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
                transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                marginRight: size === 'large' ? '4px' : 0,
              }}
            >
              <Trans>Version</Trans>
            </Typography>
            <Typography
              variant="caption1"
              color="text.secondary"
              sx={{
                marginLeft: size === 'large' ? '46px' : 0,
                transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {latestVersion}
            </Typography>
          </Box>
        </Badge>
        {showBadge && (
          <Box
            style={{
              borderRadius: '5px',
              background: 'rgba(0, 178, 169, 0.12)',
              height: '22px',
              padding: size === 'large' ? '2px 8px' : '2px 0',
            }}
          >
            {size === 'large' && (
              <Typography color="primary" variant="overline1">
                <Trans>NEW!</Trans>
              </Typography>
            )}
          </Box>
        )}
      </ButtonBase>
      <Dialog
        open={changeLogOpen}
        onClose={handleClose}
        aria-labelledby={'change-log-dialog-title'}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { maxWidth: '559px' } }}
      >
        <DialogTitle
          typography={'h3'}
          id={'change-log-dialog-title'}
          sx={{
            paddingBottom: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box flex={1} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Trans>Version</Trans> {version}
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                lineHeight: '21px',
              }}
            >
              {format(date, 'MMMM dd, yyyy')}
            </Typography>
          </Box>
          <IconButton
            aria-label={trans`close`}
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: 0 }}>
          <DialogContentText component="div">
            <Box
              sx={{
                padding: '24px 16px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Markdown
                components={{
                  ul: ({ node, ...props }) => (
                    <ul style={{ margin: 0, paddingLeft: 18 }} {...props}></ul>
                  ),
                  li: ({ children }) => (
                    <li style={{ listStyleType: 'disc' }}>
                      <Typography>{children}</Typography>
                    </li>
                  ),
                  br: ({ node, ...props }) => <Divider {...props} />,
                }}
              >
                {content}
              </Markdown>
            </Box>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChangeLogButton;
